/** Pure deep-link parsing for the EngineeringDashboard. The Home (attention)
 *  page links straight at the rows a card counted:
 *    /engineering?tab=fault            — open a tab
 *    /engineering?status=overloaded    — seed the loading table's filter
 *    /engineering?tab=feeder&feeder=12 — select a feeder and open its detail
 *  (UX spec §3.8). All read ONCE at mount; never re-read afterwards, and never
 *  written back to the URL. */

export type EngineeringTab = 'overview' | 'feeder' | 'fault' | 'ami' | 'quality'

/** Tabs that can be opened by a bare deep link. 'feeder' is excluded — it
 *  needs a cross-view feeder selection to render anything, which is what
 *  ?feeder= supplies (the dashboard passes that id in as selectedFeederId). */
const LINKABLE_TABS: readonly EngineeringTab[] = [
  'overview',
  'fault',
  'ami',
  'quality',
]

/** Status values the loading table's filter select accepts. 'divergent' is the
 *  A7 "Model disagrees" option Task 8.2 adds to that same select; listing it
 *  here early is harmless (an unmatched filter simply shows no rows until the
 *  A7 field exists, and both tasks ship in this stage). */
const LINKABLE_STATUSES: readonly string[] = [
  'overloaded',
  'heavy',
  'normal',
  'underloaded',
  'no_rating',
  'divergent',
]

export function initialEngineeringTab(
  search: string,
  selectedFeederId: number | null,
): EngineeringTab {
  const tab = new URLSearchParams(search).get('tab')
  if (tab && (LINKABLE_TABS as readonly string[]).includes(tab)) {
    return tab as EngineeringTab
  }
  return selectedFeederId ? 'feeder' : 'overview'
}

/** Seed value for TransformerLoadingTable's status filter, or undefined when
 *  the param is absent or not one the select offers. */
export function initialStatusFilter(search: string): string | undefined {
  const status = new URLSearchParams(search).get('status')
  return status && LINKABLE_STATUSES.includes(status) ? status : undefined
}

/** Feeder id from ?feeder=, or null when absent/not a positive integer. */
export function deepLinkFeederId(search: string): number | null {
  const raw = new URLSearchParams(search).get('feeder')
  if (!raw) return null
  const id = Number(raw)
  return Number.isInteger(id) && id > 0 ? id : null
}
